import { useMemo } from 'react';
import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import { useSlotGroup, useTokens, RichTextContent, EditableSlotGroup } from '@vibesites/runtime/react';
import { useScrollReveal } from '../hooks/useScrollReveal';
import { useRevealStyles } from '../hooks/useRevealStyles';
import { getAccessibleTextOnBackground } from '../utils/luminance';

// ============================================================================
// Slot group definitions (inline literals — required by the schema linter)
// ============================================================================

const ctaDef: SlotGroupDefinition = {
  label: 'CTA',
  fields: {
    url: { label: 'Button URL', type: 'url', defaultValue: '/contact-us' },
    label: { label: 'Button Label', type: 'text', defaultValue: 'Schedule a Tour' },
    _target: { label: 'Link Target', type: 'text', defaultValue: '_self' },
  },
};

// ============================================================================
// Component
// ============================================================================

export function VirtualTourIntro() {
  useRevealStyles();
  const sectionRef = useScrollReveal<HTMLElement>();

  const cta = useSlotGroup('virtualtourintro.cta', ctaDef);

  // Prefer primary on the page background; fall back when contrast is too low.
  const tokens = useTokens();
  const headingColor = useMemo(
    () => getAccessibleTextOnBackground(tokens?.color?.background, tokens?.color?.primary),
    [tokens],
  );

  return (
    <section
      ref={sectionRef}
      className="reveal-fade-up w-full bg-[var(--vibe-color-background)] pt-[40px] pb-6 md:pb-9"
    >
      <div className="mx-auto px-3 sm:px-4 lg:px-6 text-center">
        <RichTextContent
          path="virtualtourintro.heading"
          label="Heading"
          defaultValue='<p>Tour <span data-type="field-token" data-field="property_name"></span> From Anywhere</p>'
          as="h2"
          className="mx-auto max-w-4xl px-10 sm:px-12 text-balance text-[clamp(2rem,8.8vw,3.5rem)] sm:text-[clamp(1rem,4.4vw,2.5rem)] uppercase tracking-wide leading-[1.1] mb-6"
          style={{ color: headingColor, fontFamily: "'Poppins', sans-serif", fontWeight: 600 }}
        />

        <div className="max-w-3xl mx-auto md:px-10 lg:px-0">
          <RichTextContent
            path="virtualtourintro.body"
            label="Body Text"
            defaultValue={`<p>Step inside <span data-type="field-token" data-field="property_name"></span> without leaving home. Our immersive 3D virtual tours let you explore floor plans, amenities, and community spaces at your own pace, from any device. When you're ready to see it in person, schedule a tour and we'll roll out the welcome mat.</p>`}
            as="div"
            className="font-body text-base leading-relaxed text-[var(--vibe-color-text-muted)] [&_p]:mb-4 [&_a]:text-primary [&_a]:underline"
          />
        </div>

        <div className="mt-6 flex flex-wrap justify-center gap-4">
          <EditableSlotGroup groupPath="virtualtourintro.cta" definition={ctaDef}>
            <a
              href={cta.url || '/contact-us'}
              target={cta._target || '_self'}
              className="vti-cta inline-flex items-center justify-center whitespace-nowrap px-6 py-3 font-heading text-sm md:px-4 md:tracking-[0.1em] lg:px-12 lg:py-4 lg:text-base lg:tracking-[0.15em] uppercase tracking-[0.15em] transition-all"
            >
              {cta.label || 'Schedule a Tour'}
            </a>
          </EditableSlotGroup>
        </div>
      </div>

      <style>{`
        /* Solid primary pill — matches the site-wide CTA treatment. */
        .vti-cta {
          background: var(--vibe-color-primary);
          color: var(--vibe-color-text-on-primary, #fff);
          border: 1.5px solid var(--vibe-color-primary);
          border-radius: 9999px;
        }
        .vti-cta:hover {
          background: color-mix(in srgb, var(--vibe-color-primary) 88%, #000);
          border-color: color-mix(in srgb, var(--vibe-color-primary) 88%, #000);
          transform: translateY(-1px);
          box-shadow: 0 6px 18px color-mix(in srgb, var(--vibe-color-primary) 30%, transparent);
        }
      `}</style>
    </section>
  );
}

// ============================================================================
// Content Schema
// ============================================================================

export const contentSchema: ComponentContentSchema = {
  slot: 'VirtualTourIntro',
  displayName: 'Virtual Tour Intro',
  tokens: {
    'virtualtourintro.heading': {
      label: 'Heading',
      type: 'richtext',
      defaultValue: '<p>Tour <span data-type="field-token" data-field="property_name"></span> From Anywhere</p>',
    },
    'virtualtourintro.body': {
      label: 'Body Text',
      type: 'richtext',
      defaultValue:
        '<p>Step inside <span data-type="field-token" data-field="property_name"></span> without leaving home. Our immersive 3D virtual tours let you explore floor plans, amenities, and community spaces at your own pace, from any device. When you\'re ready to see it in person, schedule a tour and we\'ll roll out the welcome mat.</p>',
    },
  },
  slotGroups: {
    'virtualtourintro.cta': ctaDef,
  },
};

(VirtualTourIntro as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default VirtualTourIntro;
